import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "service";
import { toast } from "sonner";
import { IUserDetails } from "types";
import { saveTokenToLocalStore } from "utils";

export interface IFormData {
  username: string;
  password: string;
}

export interface IFacilityRoles {
  hfId: number;
  hfName: string;
  hfAddress: string;
  hfEmail: string;
  hfPhone: string;
  hfRole: string;
  hfSlug: string;
  role?: string;
}

export interface ILoginResponse {
  uid: number;
  roles: string[];
  refresh: string;
  access: string;
  user: IUserDetails;
  facilityRoles: IFacilityRoles[];
}

export const useLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<IFormData>({
    username: "",
    password: "",
  });

  const isFormComplete = () => {
    return formData.username && formData.password;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate();
  };

  const { mutate, isPending } = useMutation<ILoginResponse>({
    mutationFn: async () => {
      const response = await login(formData.username, formData.password);
      return response;
    },
    onSuccess: (data) => {
      const roles = [
        "user",
        ...data.facilityRoles
          .filter((role) => role.role !== "enduser")
          .map((role) => role.hfSlug),
      ];

      saveTokenToLocalStore("user_details", data.user);
      saveTokenToLocalStore("access_token", data.access);
      saveTokenToLocalStore("refresh_token", data.refresh);
      saveTokenToLocalStore("data", data);
      saveTokenToLocalStore("facility_roles", data.facilityRoles);
      saveTokenToLocalStore("roles", roles);

      navigate("/");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data || error.message || "Failed to login.";
      toast.error(errorMessage);
    },
  });

  return {
    formData,
    isPending,
    mutate,
    handleInputChange,
    handleSubmit,
    isFormComplete,
  };
};
